function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    const buttonName = event.currentTarget.getAttribute('name');

    if (buttonName !== 'plus' && buttonName !== 'minus') return;

    this.dataset.lastButtonName = buttonName;
    this.dataset.previousValue = previousValue;

    if (buttonName === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else if (buttonName === 'minus') {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && buttonName === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

(() => {
  const hydrateJudgeMeNativeReviewWidget = () => {
    try {
      const url = new URL(window.location.href);
      if (!url.pathname.startsWith('/products/')) return;
      if (url.pathname === '/products/garden-tunnel-den-felt-cat-cave') return;

      const wrapper = document.querySelector('.gt-pdp__judgeme #judgeme_product_reviews');
      if (!wrapper || wrapper.dataset.gtNativeReviewHydrating === 'true') return;

      const productId =
        wrapper.dataset.productId ||
        wrapper.dataset.id ||
        document.querySelector('.gt-pdp')?.dataset.productId ||
        document.querySelector('[data-product-id]')?.getAttribute('data-product-id');
      if (!productId || wrapper.dataset.gtNativeReviewHydrated === 'true') return;

      const visibleText = wrapper.textContent || '';
      const isEmptyWidget =
        !visibleText.trim() ||
        visibleText.includes('No items found') ||
        visibleText.includes('Be the first to write a review');

      const existingData =
        window.jdgm?.data?.reviewWidget?.[productId] ||
        window.jdgm?.data?.reviewWidget?.[Number(productId)];

      if (existingData?.reviews?.length && !isEmptyWidget) {
        wrapper.dataset.gtNativeReviewHydrated = 'true';
        return;
      }

      const shopDomain = window.Shopify?.shop || window.jdgm?.SHOP_DOMAIN || 'fg2dzg-1p.myshopify.com';
      const platform = window.jdgmSettings?.platform || 'shopify';
      const apiHost = (window.jdgm?.CDN_API_HOST || 'https://cdn.judge.me').replace(/\/$/, '');
      const params = new URLSearchParams({
        product_id: productId,
        page: '1',
        shop_domain: shopDomain,
        platform,
      });

      wrapper.dataset.gtNativeReviewHydrating = 'true';

      fetch(`${apiHost}/reviews/reviews_for_widget?${params.toString()}`, { credentials: 'omit' })
        .then((response) => (response.ok ? response.json() : Promise.reject(response)))
        .then((data) => {
          if (!data || Number(data.number_of_reviews || 0) <= 0 || !Array.isArray(data.reviews)) return;

          window.jdgm = window.jdgm || {};
          window.jdgm.data = window.jdgm.data || {};
          window.jdgm.data.reviewWidget = window.jdgm.data.reviewWidget || {};
          window.jdgm.data.reviewWidget[productId] = data;

          if (!isEmptyWidget && wrapper.classList.contains('jdgm-widget-revamp')) {
            wrapper.dataset.gtNativeReviewHydrated = 'true';
            return;
          }

          wrapper.innerHTML = '';
          wrapper.classList.remove('jdgm--done-setup-widget', 'jdgm-widget-revamp');

          const reviewWidgetScript = Array.from(document.scripts).find((script) =>
            script.src.includes('/review_widget.js')
          );
          const loaderScript = Array.from(document.scripts).find(
            (script) => script.src.includes('/loader.js') && script.src.includes('/judgeme-')
          );
          const scriptSrc =
            reviewWidgetScript?.src.split('?')[0] ||
            loaderScript?.src.replace(/loader\.js.*$/, 'review_widget.js');

          if (!scriptSrc) {
            wrapper.dataset.gtNativeReviewHydrated = 'true';
            return;
          }

          const script = document.createElement('script');
          script.type = 'module';
          script.src = `${scriptSrc}?gt_native_review_reload=${Date.now()}`;
          script.onload = () => {
            wrapper.dataset.gtNativeReviewHydrated = 'true';
          };
          script.onerror = () => {
            wrapper.dataset.gtNativeReviewHydrated = 'true';
          };
          document.body.appendChild(script);
        })
        .catch(() => {
          wrapper.dataset.gtNativeReviewHydrated = 'true';
        })
        .finally(() => {
          wrapper.dataset.gtNativeReviewHydrating = 'false';
        });
    } catch (error) {
      // Keep Judge.me native rendering untouched if its public CDN cannot be read.
    }
  };

  const repairNativeReviewSummaryDom = () => {
    try {
      const url = new URL(window.location.href);
      const isGardenTunnelPath = url.pathname === '/products/garden-tunnel-den-felt-cat-cave';
      const summaryEnhancedProductPaths = new Set([
        '/products/elephant-tunnel-den-felt-cat-cave',
        '/products/blue-cat-house',
        '/products/garden-tunnel-den-felt-cat-cave',
        '/products/felt-flower-cat-cave-bed',
        '/products/art-print-cat-scratcher',
        '/products/red-house-cat-cave-felt-enclosed',
        '/products/mona-lisa-cat-scratcher-bed',
        '/products/modular-felt-cat-house',
      ]);
      if (!summaryEnhancedProductPaths.has(url.pathname)) return;

      const getReviewWidgetData = () => {
        const productId =
          document.querySelector('.gt-pdp')?.dataset.productId ||
          document.querySelector('.jdgm-review-widget')?.dataset.id ||
          document.querySelector('[data-product-id]')?.getAttribute('data-product-id');
        const reviewWidgetData = window.jdgm?.data?.reviewWidget;
        if (!productId) return null;

        if (reviewWidgetData?.[productId] || reviewWidgetData?.[Number(productId)]) {
          return reviewWidgetData[productId] || reviewWidgetData[Number(productId)];
        }

        const scriptText = Array.from(document.scripts)
          .map((script) => script.textContent || '')
          .find((text) => text.includes(`jdgm.data.reviewWidget[${productId}]`));

        if (scriptText) {
          const marker = `jdgm.data.reviewWidget[${productId}] = `;
          const start = scriptText.indexOf(marker);
          const end = scriptText.indexOf(';', start + marker.length);
          if (start >= 0 && end > start) {
            try {
              return JSON.parse(scriptText.slice(start + marker.length, end));
            } catch (error) {
              return null;
            }
          }
        }

        return null;
      };

      const enhanceNativeJudgeMeSummary = (data) => {
        if (url.pathname === '/products/elephant-tunnel-den-felt-cat-cave' &&
            (!Array.isArray(data?.histogram) || !data.histogram.length)) return;
        const wrapper = document.querySelector('.gt-pdp__judgeme #judgeme_product_reviews');
        const ratingDisplay = wrapper?.querySelector('.jm-average-rating-display');
        if (!wrapper || !ratingDisplay || !data) return;

        const scoreNode = ratingDisplay.querySelector('.jm-text:first-child');
        const countNode = ratingDisplay.querySelector('.jm-text:last-child');
        const averageRating = Number.parseFloat(scoreNode?.textContent || data.average_rating || '0') || 0;
        const totalCount = Number.parseInt(data.number_of_reviews || countNode?.textContent || '0', 10) || 0;

        if (countNode && totalCount > 0) {
          countNode.textContent =
            data.review_summary_text || `Based on ${totalCount} ${totalCount === 1 ? 'review' : 'reviews'}`;
        }

        if (averageRating <= 0 || totalCount <= 0) return;

        const ratingRow = ratingDisplay.closest('.jm-cluster') || ratingDisplay;
        const summaryStack = ratingRow.parentElement;
        if (!summaryStack) return;

        const histogramData = Array.isArray(data.histogram) && data.histogram.length
          ? data.histogram
          : [5, 4, 3, 2, 1].map((rating) => ({
              rating,
              frequency: rating === Math.round(averageRating) ? totalCount : 0,
              percentage: rating === Math.round(averageRating) ? 100 : 0,
            }));
        const byRating = histogramData.reduce((memo, item) => {
          memo[Number(item.rating)] = {
            count: Number(item.frequency || 0),
            width: Number(item.percentage || 0),
          };
          return memo;
        }, {});
        const signature = `${averageRating}|${totalCount}|${[5, 4, 3, 2, 1]
          .map((rating) => `${byRating[rating]?.count || 0}:${byRating[rating]?.width || 0}`)
          .join(',')}`;

        let histogram = summaryStack.querySelector(':scope > .gt-pdp__jm-histogram');
        if (!histogram) {
          histogram = document.createElement('div');
          histogram.className = 'gt-pdp__jm-histogram';
          histogram.setAttribute('aria-hidden', 'true');
          ratingRow.insertAdjacentElement('afterend', histogram);
        }

        if (histogram.dataset.signature === signature) return;

        histogram.dataset.signature = signature;
        histogram.innerHTML = [5, 4, 3, 2, 1].map((rating) => {
          const count = byRating[rating]?.count || 0;
          const width = byRating[rating]?.width || 0;
          return `
            <div class="gt-pdp__jm-histogram-row">
              <span class="gt-pdp__jm-histogram-rating">${rating}</span>
              <span class="gt-pdp__jm-histogram-star">★</span>
              <span class="gt-pdp__jm-histogram-track"><span class="gt-pdp__jm-histogram-fill" style="--gt-jm-bar: ${width}%"></span></span>
              <span class="gt-pdp__jm-histogram-count">${count}</span>
            </div>
          `;
        }).join('');
      };

      const scheduleNativeJudgeMeSummaryEnhancement = (data) => {
        [0, 250, 800, 1600, 3000].forEach((delay) => {
          window.setTimeout(() => enhanceNativeJudgeMeSummary(data), delay);
        });
      };

      const ensureNativeJudgeMeReviews = () => {
        const wrapper = document.querySelector('.gt-pdp__judgeme #judgeme_product_reviews');
        if (!wrapper || wrapper.dataset.gtReviewHydrating === 'true') return;

        const productId = wrapper.dataset.productId || wrapper.dataset.id;
        if (!productId) return;
        if (wrapper.dataset.gtReviewHydrated === 'true') {
          if (isGardenTunnelPath) {
            const existingData = getReviewWidgetData();
            if (existingData) enhanceNativeJudgeMeSummary(existingData);
          }
          return;
        }

        const visibleText = wrapper.textContent || '';
        const isEmptyWidget =
          !visibleText.trim() ||
          visibleText.includes('No items found') ||
          visibleText.includes('Be the first to write a review');
        const existingData = getReviewWidgetData();

        if (existingData?.reviews?.length && !isEmptyWidget) {
          scheduleNativeJudgeMeSummaryEnhancement(existingData);
          wrapper.dataset.gtReviewHydrated = 'true';
          return;
        }

        const shopDomain = window.Shopify?.shop || window.jdgm?.SHOP_DOMAIN || 'fg2dzg-1p.myshopify.com';
        const platform = window.jdgmSettings?.platform || 'shopify';
        const apiHost = (window.jdgm?.CDN_API_HOST || 'https://cdn.judge.me').replace(/\/$/, '');
        const params = new URLSearchParams({
          product_id: productId,
          page: '1',
          shop_domain: shopDomain,
          platform,
        });

        wrapper.dataset.gtReviewHydrating = 'true';

        fetch(`${apiHost}/reviews/reviews_for_widget?${params.toString()}`, { credentials: 'omit' })
          .then((response) => (response.ok ? response.json() : Promise.reject(response)))
          .then((data) => {
            if (!data || Number(data.number_of_reviews || 0) <= 0) return;

            window.jdgm = window.jdgm || {};
            window.jdgm.data = window.jdgm.data || {};
            window.jdgm.data.reviewWidget = window.jdgm.data.reviewWidget || {};
            window.jdgm.data.reviewWidget[productId] = data;

            const shouldRerender =
              isGardenTunnelPath &&
              (isEmptyWidget || !wrapper.innerHTML.trim() || !wrapper.classList.contains('jdgm-widget-revamp'));

            if (!shouldRerender) {
              scheduleNativeJudgeMeSummaryEnhancement(data);
              wrapper.dataset.gtReviewHydrated = 'true';
              return;
            }

            wrapper.innerHTML = '';
            wrapper.classList.remove('jdgm--done-setup-widget', 'jdgm-widget-revamp');

            const reviewWidgetScript = Array.from(document.scripts).find((script) =>
              script.src.includes('/review_widget.js')
            );
            const loaderScript = Array.from(document.scripts).find(
              (script) => script.src.includes('/loader.js') && script.src.includes('/judgeme-')
            );
            const scriptSrc =
              reviewWidgetScript?.src.split('?')[0] ||
              loaderScript?.src.replace(/loader\.js.*$/, 'review_widget.js');

            if (!scriptSrc) {
              wrapper.dataset.gtReviewHydrated = 'true';
              return;
            }

            const script = document.createElement('script');
            script.type = 'module';
            script.src = `${scriptSrc}?gt_review_reload=${Date.now()}`;
            script.onload = () => {
              scheduleNativeJudgeMeSummaryEnhancement(data);
              wrapper.dataset.gtReviewHydrated = 'true';
            };
            script.onerror = () => {
              scheduleNativeJudgeMeSummaryEnhancement(data);
              wrapper.dataset.gtReviewHydrated = 'true';
            };
            document.body.appendChild(script);
          })
          .catch(() => {
            wrapper.dataset.gtReviewHydrated = 'true';
          })
          .finally(() => {
            wrapper.dataset.gtReviewHydrating = 'false';
          });
      };

      ensureNativeJudgeMeReviews();
      if (!isGardenTunnelPath) return;

      const applyGardenTunnelHeroSpacingLock = () => {
        const desktopPadding = '0 40px 0';
        const mobilePadding = '0 0 32px';
        const isMobile = window.matchMedia('(max-width: 900px)').matches;
        document.querySelectorAll('.gt-pdp__hero').forEach((hero) => {
          hero.style.setProperty('padding', isMobile ? mobilePadding : desktopPadding, 'important');
        });
      };

      if (!document.getElementById('gt-pdp-global-wildone-repair-20260523')) {
        const style = document.createElement('style');
        style.id = 'gt-pdp-global-wildone-repair-20260523';
        style.textContent = `
          html body .shopify-section .gt-pdp .gt-pdp__hero {
            margin-top: 0 !important;
            padding: 0 40px 0 !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__hero-grid {
            align-items: start !important;
            display: grid !important;
            gap: 20px !important;
            grid-template-columns: 1fr 1fr !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__gallery {
            display: grid !important;
            gap: 8px !important;
            grid-template-columns: 1fr !important;
            min-height: 0 !important;
            overflow: visible !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__gallery-frame,
          html body .shopify-section .gt-pdp .gt-pdp__gallery-img {
            aspect-ratio: 1 / 1 !important;
            height: auto !important;
            min-height: 0 !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__product-info-wrap {
            align-self: stretch !important;
            display: block !important;
            min-width: 0 !important;
            padding: 15px 0 0 !important;
            position: relative !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__product-info,
          html body .shopify-section .gt-pdp .gt-pdp__product-info.is-fixed,
          html body .shopify-section .gt-pdp .gt-pdp__product-info.is-bottom {
            align-self: start !important;
            bottom: auto !important;
            left: auto !important;
            max-height: none !important;
            overflow: visible !important;
            padding: 0 0 28px !important;
            position: sticky !important;
            top: calc(var(--header-height, 88px) + 15px) !important;
            width: auto !important;
            z-index: 30 !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__breadcrumb,
          html body .shopify-section .gt-pdp .gt-pdp__eyebrow {
            align-items: center !important;
            background: #CCBCA8 !important;
            border-radius: 3px !important;
            color: #593518 !important;
            display: inline-flex !important;
            font-size: 10px !important;
            letter-spacing: 0.12em !important;
            line-height: 1.2 !important;
            margin: 0 0 10px !important;
            padding: 5px 12px !important;
            text-transform: uppercase !important;
            width: fit-content !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__product-info h1 {
            font-family: Georgia, "Times New Roman", serif !important;
            font-size: 30px !important;
            font-weight: 400 !important;
            letter-spacing: 0 !important;
            line-height: 1.05 !important;
            margin: 0 0 10px !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__title-meta-row {
            align-items: center !important;
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 10px !important;
            justify-content: flex-start !important;
            margin: 0 0 10px !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__lead-inline {
            color: rgba(89, 53, 24, 0.78) !important;
            font-family: Georgia, "Times New Roman", serif !important;
            font-size: 14px !important;
            font-style: italic !important;
            letter-spacing: 0 !important;
            line-height: 1.35 !important;
            margin: 0 !important;
            text-align: left !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__reviews-inline,
          html body .shopify-section .gt-pdp .gt-pdp__reviews-inline * {
            font-size: 13px !important;
            line-height: 1.2 !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__reviews-inline .jdgm-star,
          html body .shopify-section .gt-pdp .gt-pdp__reviews-inline .jdgm-star::before,
          html body .shopify-section .gt-pdp .gt-pdp__reviews-inline .jdgm-prev-badge__stars,
          html body .shopify-section .gt-pdp .gt-pdp__reviews-inline .jdgm-prev-badge__stars .jdgm-star,
          html body .shopify-section .gt-pdp .gt-pdp__reviews-inline .jdgm-prev-badge__stars .jdgm-star::before {
            color: #593518 !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__price {
            font-family: Georgia, "Times New Roman", serif !important;
            font-size: 20px !important;
            line-height: 1 !important;
            margin: 0 0 22px !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__variant-group {
            margin: 0 !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__variant-label {
            color: rgba(89, 53, 24, 0.68) !important;
            display: block !important;
            font-size: 11px !important;
            letter-spacing: 0.16em !important;
            line-height: 1.2 !important;
            margin: 0 0 10px !important;
            text-transform: uppercase !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__variant-options {
            display: grid !important;
            gap: 12px !important;
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            margin: 0 !important;
            width: 100% !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__variant-btn {
            align-items: center !important;
            display: flex !important;
            flex-direction: row !important;
            gap: 10px !important;
            justify-content: space-between !important;
            min-height: 46px !important;
            padding: 0 14px !important;
            width: 100% !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__variant-btn span,
          html body .shopify-section .gt-pdp .gt-pdp__variant-title {
            font-size: 14px !important;
            font-weight: 500 !important;
            line-height: 1.2 !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__variant-btn small,
          html body .shopify-section .gt-pdp .gt-pdp__variant-size {
            color: rgba(89, 53, 24, 0.68) !important;
            font-size: 12px !important;
            line-height: 1.2 !important;
            margin: 0 !important;
            text-align: right !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__buy-row {
            display: grid !important;
            gap: 12px !important;
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            margin: 16px 0 10px !important;
            width: 100% !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__buy-row > *,
          html body .shopify-section .gt-pdp .gt-pdp__cta-add,
          html body .shopify-section .gt-pdp .gt-pdp__shop-pay-wrap .shopify-payment-button__button,
          html body .shopify-section .gt-pdp .shopify-payment-button__button {
            height: 48px !important;
            min-height: 48px !important;
            width: 100% !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__cta-add {
            font-size: 13px !important;
            letter-spacing: 0.08em !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__shop-pay-wrap .shopify-payment-button__more-options {
            display: block !important;
            font-size: 11px !important;
            line-height: 1.3 !important;
            margin: 10px auto 0 !important;
            text-align: center !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__trust-mini {
            column-gap: 30px !important;
            display: grid !important;
            grid-template-columns: repeat(3, max-content) !important;
            justify-content: space-between !important;
            margin: 0 !important;
            padding: 16px 0 !important;
            row-gap: 12px !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item {
            align-items: center !important;
            display: grid !important;
            gap: 0 14px !important;
            grid-template-columns: 35px max-content !important;
            justify-content: flex-start !important;
            min-width: 0 !important;
            padding: 0 !important;
            text-align: left !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item:nth-child(n + 2) {
            grid-template-columns: 35px max-content !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item > svg,
          html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item > picture,
          html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item::before,
          html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item::after {
            content: none !important;
            display: none !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item > img,
          html body .shopify-section .gt-pdp .gt-pdp__trust-icon {
            display: block !important;
            flex: 0 0 35px !important;
            height: 35px !important;
            margin: 0 !important;
            max-height: 35px !important;
            max-width: 35px !important;
            object-fit: contain !important;
            width: 35px !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item:first-child > img,
          html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item:first-child .gt-pdp__trust-icon {
            flex: 0 0 35px !important;
            height: 35px !important;
            margin: 0 !important;
            max-height: 35px !important;
            max-width: 35px !important;
            transform: none !important;
            width: 35px !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__trust-title {
            display: block !important;
            font-size: 10px !important;
            letter-spacing: 0.08em !important;
            line-height: 1.2 !important;
            text-transform: uppercase !important;
            white-space: nowrap !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__trust-sub {
            font-size: 11px !important;
            line-height: 1.35 !important;
            margin-top: 4px !important;
            white-space: nowrap !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__trust-copy {
            display: block !important;
            flex: 1 1 auto !important;
            min-width: 0 !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__accordion-block {
            margin-top: 0 !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__accordion-item:first-child {
            border-top: 0 !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__accordion-item {
            padding: 0 !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__accordion-item summary {
            font-size: 14px !important;
            font-weight: 500 !important;
            padding: 10px 0 !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__features {
            clear: both !important;
            order: 3 !important;
            padding: 0 0 40px !important;
            position: relative !important;
            z-index: 1 !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__features-heading {
            margin-left: auto !important;
            margin-right: auto !important;
            padding: 40px 0 32px !important;
            width: calc(100% - 80px) !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__features-grid {
            margin-left: auto !important;
            margin-right: auto !important;
            padding: 0 !important;
            width: calc(100% - 80px) !important;
          }
          html body .shopify-section .gt-pdp {
            display: flex !important;
            flex-direction: column !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__hero {
            order: 1 !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__ugc {
            margin: 80px 0 0 !important;
            max-width: none !important;
            order: 2 !important;
            padding: 0 40px 40px !important;
            text-align: center !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__related {
            order: 4 !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__judgeme {
            order: 5 !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__ugc-grid {
            display: grid !important;
            gap: 12px !important;
            grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
            margin-top: 24px !important;
          }
          html body .shopify-section .gt-pdp .gt-pdp__ugc-video,
          html body .shopify-section .gt-pdp .gt-pdp__ugc-image,
          html body .shopify-section .gt-pdp .gt-pdp__ugc-placeholder {
            aspect-ratio: 3 / 4 !important;
            width: 100% !important;
          }
          @media (max-width: 900px) {
            html body .shopify-section .gt-pdp .gt-pdp__hero {
              padding: 0 0 32px !important;
            }
            html body .shopify-section .gt-pdp .gt-pdp__hero-grid {
              display: block !important;
            }
            html body .shopify-section .gt-pdp .gt-pdp__product-info-wrap {
              padding: 32px 24px 32px !important;
            }
            html body .shopify-section .gt-pdp .gt-pdp__product-info,
            html body .shopify-section .gt-pdp .gt-pdp__product-info.is-fixed,
            html body .shopify-section .gt-pdp .gt-pdp__product-info.is-bottom {
              max-height: none !important;
              overflow: visible !important;
              padding: 0 !important;
              position: relative !important;
              top: auto !important;
            }
            html body .shopify-section .gt-pdp .gt-pdp__ugc {
              margin: 20px 0 0 !important;
              padding: 0 20px 32px !important;
            }
            html body .shopify-section .gt-pdp .gt-pdp__features {
              padding: 0 0 40px !important;
            }
            html body .shopify-section .gt-pdp .gt-pdp__features-heading {
              padding: 20px 0 28px !important;
              width: calc(100% - 40px) !important;
            }
            html body .shopify-section .gt-pdp .gt-pdp__features-grid {
              width: calc(100% - 40px) !important;
            }
            html body .shopify-section .gt-pdp .gt-pdp__ugc-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
            html body .shopify-section .gt-pdp .gt-pdp__trust-mini {
              grid-template-columns: 1fr !important;
              justify-content: stretch !important;
            }
            html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item {
              grid-template-columns: 35px minmax(0, 1fr) !important;
            }
            html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item:nth-child(n + 2) {
              grid-template-columns: 35px minmax(0, 1fr) !important;
            }
          }
          @media (max-width: 480px) {
            html body .shopify-section .gt-pdp .gt-pdp__buy-row,
            html body .shopify-section .gt-pdp .gt-pdp__variant-options {
              grid-template-columns: 1fr !important;
            }
          }
        `;
        document.head.appendChild(style);
      }
      applyGardenTunnelHeroSpacingLock();

      document.querySelectorAll('.gt-pdp__product-info.is-fixed, .gt-pdp__product-info.is-bottom').forEach((node) => {
        node.style.removeProperty('bottom');
        node.style.removeProperty('left');
        node.style.removeProperty('max-height');
        node.style.removeProperty('position');
        node.style.removeProperty('top');
        node.style.removeProperty('width');
      });

      const currentUgcSection = document.querySelector('.gt-pdp__ugc');
      const currentFeaturesSection = document.querySelector('.gt-pdp__features');
      const ugcNeedsMove =
        currentUgcSection &&
        currentFeaturesSection &&
        currentUgcSection.nextElementSibling !== currentFeaturesSection;
      const trustItems = Array.from(document.querySelectorAll('.gt-pdp__trust-mini-item'));
      const trustNeedsRepair =
        !document.querySelector('.gt-pdp__trust-title') ||
        trustItems.some((item) =>
          item.querySelector('svg, picture, img:not(.gt-pdp__trust-icon)') ||
          item.querySelectorAll('.gt-pdp__trust-icon').length !== 1
        );
      const galleryImageCount = document.querySelectorAll('.gt-pdp__gallery img').length;
      const galleryNeedsRepair = galleryImageCount !== 3;
      const ugcPrimaryNeedsRepair = !document.querySelector('.gt-pdp__ugc-video--primary');
      const ugcSecondaryNeedsRepair = !document.querySelector('.gt-pdp__ugc-video--secondary');
      const ugcTertiaryNeedsRepair = !document.querySelector('.gt-pdp__ugc-video--tertiary');
      const ugcQuaternaryNeedsRepair = !document.querySelector('.gt-pdp__ugc-video--quaternary');
      const reviewsNeedsRepair = !document.querySelector('#judgeme_product_reviews[data-product-handle]');

      const staleGardenTunnelDom =
        document.querySelector('.gt-pdp__features-title') ||
        document.querySelector('span.gt-pdp__selected-val') ||
        !document.querySelector('.gt-pdp__judgeme-shell') ||
        !currentUgcSection ||
        ugcNeedsMove ||
        trustNeedsRepair ||
        galleryNeedsRepair ||
        ugcPrimaryNeedsRepair ||
        ugcSecondaryNeedsRepair ||
        ugcTertiaryNeedsRepair ||
        ugcQuaternaryNeedsRepair ||
        reviewsNeedsRepair ||
        /\.$/.test(document.querySelector('.gt-pdp__product-info h1')?.textContent?.trim() || '');

      if (!staleGardenTunnelDom) return;

      const selectedVariantText = document.querySelector('span.gt-pdp__selected-val');
      selectedVariantText?.remove();

      window.__gtGardenTunnelRepairVersion = '20260524-images-price-related';

      document.querySelectorAll('.gt-pdp__breadcrumb, .gt-pdp__eyebrow').forEach((badge) => {
        badge.textContent = 'BESTSELLER';
        badge.style.alignItems = 'center';
        badge.style.background = '#CCBCA8';
        badge.style.borderRadius = '3px';
        badge.style.color = '#593518';
        badge.style.display = 'inline-flex';
        badge.style.fontSize = '10px';
        badge.style.letterSpacing = '0.12em';
        badge.style.lineHeight = '1.2';
        badge.style.margin = '0 0 10px';
        badge.style.padding = '5px 12px';
        badge.style.textTransform = 'uppercase';
        badge.style.width = 'fit-content';
      });

      const themeAssetBase = Array.from(document.scripts)
        .map((script) => script.src || '')
        .find((src) => src.includes('/assets/global.js'))
        ?.replace(/global\.js.*$/, '');
      const createGardenTunnelUgcVideo = ({ modifier, poster, src }) => {
        if (!themeAssetBase) return null;

        const video = document.createElement('video');
        const source = document.createElement('source');

        video.className = `gt-pdp__ugc-video gt-pdp__ugc-video--${modifier}`;
        video.autoplay = false;
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.preload = 'none';
        video.poster = `${themeAssetBase}${poster}`;
        source.src = `${themeAssetBase}${src}`;
        source.type = 'video/mp4';
        video.appendChild(source);

        return video;
      };
      const createPrimaryUgcVideo = () =>
        createGardenTunnelUgcVideo({
          modifier: 'primary',
          poster: 'garden-tunnel-ugc-10-20260727-poster.jpg',
          src: 'garden-tunnel-ugc-10-20260727.mp4',
        });
      const createSecondaryUgcVideo = () =>
        createGardenTunnelUgcVideo({
          modifier: 'secondary',
          poster: 'garden-tunnel-adventure-ugc-2-20260524-poster.jpg',
          src: 'garden-tunnel-adventure-ugc-2-20260524.mp4',
        });
      const createTertiaryUgcVideo = () =>
        createGardenTunnelUgcVideo({
          modifier: 'tertiary',
          poster: 'garden-tunnel-adventure-ugc-3-20260526-poster.jpg',
          src: 'garden-tunnel-adventure-ugc-3-20260526.mp4',
        });
      const createQuaternaryUgcVideo = () =>
        createGardenTunnelUgcVideo({
          modifier: 'quaternary',
          poster: 'garden-tunnel-real-homes-ugc-4-20260811-poster.jpg',
          src: 'garden-tunnel-real-homes-ugc-4-20260811.mp4',
        });
      if (themeAssetBase) {
        const setGardenTunnelImage = (container, item, options = {}) => {
          if (!container || !item) return;

          container.querySelectorAll('video').forEach((video) => video.remove());
          let image = container.querySelector('img');
          if (!image) {
            image = document.createElement('img');
            container.appendChild(image);
          }

          image.classList.add('gt-pdp__image');
          image.removeAttribute('srcset');
          const desiredSrc = item.src || `${themeAssetBase}${item.asset}`;
          const existingSrc = image.getAttribute('src');
          const sameAsset = !item.src && existingSrc && new URL(existingSrc, location.href).pathname === new URL(desiredSrc, location.href).pathname;
          if (!sameAsset) image.src = desiredSrc;
          image.alt = item.alt;
          image.loading = options.loading || 'lazy';
          image.sizes = options.sizes || '(min-width: 901px) 50vw, 100vw';
          image.width = 1254;
          image.height = 1254;
          if (options.decoding) image.decoding = options.decoding;
        };

        const setGardenTunnelFeatureVideo = (container, item) => {
          if (!container || !item) return;

          container.querySelectorAll('img').forEach((image) => image.remove());
          let video = container.querySelector('video');
          if (!video) {
            video = document.createElement('video');
            container.appendChild(video);
          }

          video.className = 'gt-pdp__image gt-pdp__feat-video';
          video.autoplay = false;
          video.muted = true;
          video.defaultMuted = true;
          video.loop = true;
          video.playsInline = true;
          video.preload = 'none';
          video.poster = `${themeAssetBase}${item.poster}`;
          video.setAttribute('aria-label', item.alt);
          let source = video.querySelector('source');
          if (!source) {
            source = document.createElement('source');
            video.appendChild(source);
          }
          source.src = `${themeAssetBase}${item.asset}`;
          source.type = 'video/mp4';
        };

        const featureItems = [
          {
            asset: 'prowlet-garden-tunnel-key-feature-large-real-home-20260825.png',
            alt: 'Two cats playing on the Garden Tunnel Den in a real living room',
          },
          {
            asset: 'prowlet-garden-peek-play-20261029.png',
            alt: 'Cat peeking through a flower-shaped opening while playing with a feather wand on the Garden Tunnel Den',
          },
          {
            src: 'https://cdn.shopify.com/s/files/1/0945/2771/7672/files/prowlet-garden-tunnel-key-feature-hideaway-angle-20260827.jpg?v=1787771674',
            alt: 'Cat crouching inside the sheltered Garden Tunnel Den',
          },
          {
            asset: 'prowlet-garden-felt-closeup-20261030.jpg',
            alt: 'Close-up of 9mm structured felt panels and connectors on the Garden Tunnel Den',
          },
          {
            asset: 'prowlet-garden-round-chase-20261030.jpg',
            alt: 'Inner ring chase track inside the Garden Tunnel Den felt cat cave',
          },
        ];
        const featureTargets = [
          document.querySelector('.gt-pdp__feat-large'),
          ...Array.from(document.querySelectorAll('.gt-pdp__feat-img')).slice(0, 4),
        ];
        featureTargets.forEach((target, index) => {
          if (featureItems[index]?.type === 'video') {
            setGardenTunnelFeatureVideo(target, featureItems[index]);
            return;
          }
          setGardenTunnelImage(target, featureItems[index], {
            sizes: index === 0 ? '(min-width: 901px) 50vw, 100vw' : '(min-width: 901px) 25vw, 50vw',
          });
        });
      }

      const galleryForCleanup = document.querySelector('.gt-pdp__gallery');
      if (galleryForCleanup) {
        Array.from(galleryForCleanup.querySelectorAll('.gt-pdp__gallery-frame:not(.gt-pdp__gallery-clone)'))
          .slice(6)
          .forEach((frame) => {
            frame.remove();
          });
      }

      if (themeAssetBase) {
        [
          { icon: 'prowlet-trust-free-shipping-cat-back.png', title: 'PEEKABOO DESIGN', sub: 'Made for curious paws' },
          { icon: 'prowlet-trust-30-day-returns-cat-box.png', title: '30-DAY RETURNS', sub: 'If unused, send it back' },
          {
            icon: 'prowlet-trust-heavyweight-felt-cat-orange-box.png',
            title: 'HEAVYWEIGHT FELT',
            sub: '9mm dense build',
          },
        ].forEach((item, index) => {
          const trustItem = document.querySelectorAll('.gt-pdp__trust-mini-item')[index];
          if (!trustItem) return;
          const iconSize = 35;

          const image = document.createElement('img');
          const existingIconSrc = trustItem.querySelector('img')?.getAttribute('src');
          const iconSrc = `${themeAssetBase}${item.icon}`;
          image.src = existingIconSrc && new URL(existingIconSrc, location.href).pathname === new URL(iconSrc, location.href).pathname ? existingIconSrc : iconSrc;
          image.alt = '';
          image.className = 'gt-pdp__trust-icon';
          image.setAttribute('aria-hidden', 'true');
          image.loading = 'lazy';
          image.width = iconSize;
          image.height = iconSize;

          const title = document.createElement('span');
          title.className = 'gt-pdp__trust-title';
          title.textContent = item.title;

          const sub = document.createElement('span');
          sub.className = 'gt-pdp__trust-sub';
          sub.textContent = item.sub;

          const copy = document.createElement('span');
          copy.className = 'gt-pdp__trust-copy';
          copy.append(title, sub);

          trustItem.replaceChildren(image, copy);
        });
      }

      const titleMetaRow = document.querySelector('.gt-pdp__title-meta-row');
      const leadInline = titleMetaRow?.querySelector('.gt-pdp__lead-inline');
      let reviewsInline = titleMetaRow?.querySelector('.gt-pdp__reviews-inline');

      if (reviewsInline && reviewsInline.tagName !== 'A') {
        const reviewsLink = document.createElement('a');
        reviewsLink.className = `${reviewsInline.className} gt-pdp__reviews-link`;
        reviewsLink.href = '#reviews';
        reviewsLink.setAttribute('aria-label', 'Read Garden Tunnel Den reviews');
        while (reviewsInline.firstChild) reviewsLink.appendChild(reviewsInline.firstChild);
        reviewsInline.replaceWith(reviewsLink);
        reviewsInline = reviewsLink;
      }

      if (titleMetaRow && leadInline && reviewsInline) {
        titleMetaRow.style.justifyContent = 'flex-start';
        titleMetaRow.style.gap = '10px';
        titleMetaRow.style.flexWrap = 'wrap';
        leadInline.style.textAlign = 'left';
        titleMetaRow.append(leadInline, reviewsInline);
      }

      const productHeading = document.querySelector('.gt-pdp__product-info h1');
      if (productHeading) {
        productHeading.textContent = productHeading.textContent.replace(/\.$/, '');
        productHeading.style.fontSize = '30px';
        productHeading.style.marginBottom = '10px';
      }

      const variantOptions = document.querySelector('.gt-pdp__variant-options');
      if (variantOptions) {
        variantOptions.style.display = 'grid';
        variantOptions.style.gap = '12px';
        variantOptions.style.gridTemplateColumns = 'repeat(2, minmax(0, 1fr))';
        variantOptions.style.width = '100%';
      }

      const buyRow = document.querySelector('.gt-pdp__buy-row');
      if (buyRow) {
        buyRow.style.display = 'grid';
        buyRow.style.gap = '12px';
        buyRow.style.gridTemplateColumns = 'repeat(2, minmax(0, 1fr))';
        buyRow.style.width = '100%';
      }

      const featuresHeading = document.querySelector('.gt-pdp__features-heading');
      if (featuresHeading && !featuresHeading.querySelector('.gt-pdp__section-title')) {
        featuresHeading.innerHTML = '<h2 class="gt-pdp__section-title">Key <em class="gt-pdp__title-accent">features</em></h2>';
      }

      const firstAccordionItem = document.querySelector('.gt-pdp__accordion-block .gt-pdp__accordion-item');
      if (firstAccordionItem) firstAccordionItem.style.borderTop = '0';

      document.querySelectorAll('.gt-pdp__accordion-item').forEach((item) => {
        item.style.padding = '0';
      });

      document.querySelectorAll('.gt-pdp__accordion-item summary').forEach((summary) => {
        summary.style.fontSize = '14px';
        summary.style.fontWeight = '500';
        summary.style.padding = '10px 0';
      });

      document.querySelectorAll('.gt-pdp__accordion-content').forEach((content) => {
        content.style.marginTop = '0';
        content.style.paddingBottom = '10px';
      });

      const relatedOrder = [
        'modular-felt-cat-house',
        'garden-playmat-foldable-cat-mat-tunnel',
        'mona-lisa-cat-scratcher-bed',
      ];
      const relatedGrid = document.querySelector('.gt-pdp__related-grid');
      if (relatedGrid) {
        const formatProwletMoney = (priceCents) => {
          try {
            return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
              currency: window.Shopify?.currency?.active || 'USD',
              style: 'currency',
            }).format(priceCents / 100);
          } catch (error) {
            return `$${(priceCents / 100).toFixed(2)}`;
          }
        };
        const normalizeMonaLisaRelatedCard = (card) => {
          if (card.dataset.productHandle !== 'mona-lisa-cat-scratcher-bed') return;
          card.dataset.priceCents = '15900';
          const price = card.querySelector('.gt-pdp__related-price');
          if (price && !price.textContent.includes('Full Set')) {
            price.textContent = `Full Set — ${formatProwletMoney(15900)}`;
          }
        };
        const relatedCards = Array.from(relatedGrid.querySelectorAll('.gt-pdp__related-card'));
        relatedCards.forEach(normalizeMonaLisaRelatedCard);
        const cardsByHandle = new Map(relatedCards.map((card) => [card.dataset.productHandle, card]));
        const orderedCards = relatedOrder.map((handle) => cardsByHandle.get(handle)).filter(Boolean);

        orderedCards.forEach((card, index) => {
          card.hidden = false;
          card.dataset.relatedOrder = String(index);
          relatedGrid.appendChild(card);
        });

        relatedCards.forEach((card) => {
          if (!orderedCards.includes(card)) card.hidden = true;
        });
      }

      const featuresSection = document.querySelector('.gt-pdp__features');
      const existingUgcSection = document.querySelector('.gt-pdp__ugc');
      if (featuresSection && existingUgcSection && existingUgcSection.nextElementSibling !== featuresSection) {
        featuresSection.before(existingUgcSection);
      }

      const judgeMeSection = document.querySelector('.gt-pdp__judgeme');
      const ugcInsertTarget = featuresSection || judgeMeSection;
      const ugcGrid = document.querySelector('.gt-pdp__ugc-grid');
      const primaryUgcVideo = createPrimaryUgcVideo();
      if (ugcGrid && primaryUgcVideo && !ugcGrid.querySelector('.gt-pdp__ugc-video--primary')) {
        if (ugcGrid.firstElementChild) {
          ugcGrid.firstElementChild.replaceWith(primaryUgcVideo);
        } else {
          ugcGrid.prepend(primaryUgcVideo);
        }
        while (ugcGrid.children.length > 4) {
          ugcGrid.lastElementChild?.remove();
        }
      }
      const secondaryUgcVideo = createSecondaryUgcVideo();
      if (ugcGrid && secondaryUgcVideo && !ugcGrid.querySelector('.gt-pdp__ugc-video--secondary')) {
        if (ugcGrid.children[1]) {
          ugcGrid.children[1].replaceWith(secondaryUgcVideo);
        } else {
          ugcGrid.append(secondaryUgcVideo);
        }
        while (ugcGrid.children.length > 4) {
          ugcGrid.lastElementChild?.remove();
        }
      }
      const tertiaryUgcVideo = createTertiaryUgcVideo();
      if (ugcGrid && tertiaryUgcVideo && !ugcGrid.querySelector('.gt-pdp__ugc-video--tertiary')) {
        if (ugcGrid.children[2]) {
          ugcGrid.children[2].replaceWith(tertiaryUgcVideo);
        } else {
          ugcGrid.append(tertiaryUgcVideo);
        }
        while (ugcGrid.children.length > 4) {
          ugcGrid.lastElementChild?.remove();
        }
      }
      const quaternaryUgcVideo = createQuaternaryUgcVideo();
      if (ugcGrid && quaternaryUgcVideo && !ugcGrid.querySelector('.gt-pdp__ugc-video--quaternary')) {
        if (ugcGrid.children[3]) {
          ugcGrid.children[3].replaceWith(quaternaryUgcVideo);
        } else {
          ugcGrid.append(quaternaryUgcVideo);
        }
        while (ugcGrid.children.length > 4) {
          ugcGrid.lastElementChild?.remove();
        }
      }
      const primaryUgcVideoForMarkup = createPrimaryUgcVideo();
      const secondaryUgcVideoForMarkup = createSecondaryUgcVideo();
      const tertiaryUgcVideoForMarkup = createTertiaryUgcVideo();
      const quaternaryUgcVideoForMarkup = createQuaternaryUgcVideo();
      const ugcPrimaryVideoMarkup = primaryUgcVideoForMarkup
        ? primaryUgcVideoForMarkup.outerHTML
        : '<div class="gt-pdp__ugc-placeholder" aria-hidden="true"></div>';
      const ugcSecondaryVideoMarkup = secondaryUgcVideoForMarkup
        ? secondaryUgcVideoForMarkup.outerHTML
        : '<div class="gt-pdp__ugc-placeholder" aria-hidden="true"></div>';
      const ugcTertiaryVideoMarkup = tertiaryUgcVideoForMarkup
        ? tertiaryUgcVideoForMarkup.outerHTML
        : '<div class="gt-pdp__ugc-placeholder" aria-hidden="true"></div>';
      const ugcQuaternaryVideoMarkup = quaternaryUgcVideoForMarkup
        ? quaternaryUgcVideoForMarkup.outerHTML
        : '<div class="gt-pdp__ugc-placeholder" aria-hidden="true"></div>';
      if (ugcInsertTarget && !document.querySelector('.gt-pdp__ugc')) {
        const ugcSection = document.createElement('section');
        ugcSection.className = 'gt-pdp__ugc';
        ugcSection.innerHTML = `
          <h2 class="gt-pdp__section-title">
            Turn playtime into an <em class="gt-pdp__title-accent">adventure</em>.
          </h2>
          <div class="gt-pdp__ugc-grid">
            ${ugcPrimaryVideoMarkup}
            ${ugcSecondaryVideoMarkup}
            ${ugcTertiaryVideoMarkup}
            ${ugcQuaternaryVideoMarkup}
          </div>
        `;
        ugcInsertTarget.before(ugcSection);
      }
      applyGardenTunnelHeroSpacingLock();

      if (url.searchParams.get('variant') === '51619225665832') {
        url.searchParams.delete('variant');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }
    } catch (error) {
      // Leave the page untouched if URL parsing is blocked.
    }
  };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          hydrateJudgeMeNativeReviewWidget();
          repairNativeReviewSummaryDom();
        }, { once: true });
      } else {
        hydrateJudgeMeNativeReviewWidget();
        repairNativeReviewSummaryDom();
      }

  [350, 1500, 3500].forEach((delay) => {
    window.setTimeout(hydrateJudgeMeNativeReviewWidget, delay);
    window.setTimeout(repairNativeReviewSummaryDom, delay);
  });

  if (window.location.pathname === '/products/garden-tunnel-den-felt-cat-cave') {
    window.addEventListener('load', repairNativeReviewSummaryDom, { once: true });
    [6000, 9000, 12000].forEach((delay) => {
      window.setTimeout(repairNativeReviewSummaryDom, delay);
    });
  }
})();

(() => {
  const restoreNonGardenPdpGallery = async () => {
    const pdp = document.querySelector('.gt-pdp');
    const gallery = pdp?.querySelector('.gt-pdp__gallery');
    const handle = pdp?.dataset.productHandle;
    if (!pdp || !gallery || !handle || handle === 'garden-tunnel-den-felt-cat-cave') return;

    const hasGardenImages = Array.from(gallery.querySelectorAll('img')).some((image) =>
      (image.currentSrc || image.src || '').includes('garden-tunnel-gallery-')
    );
    if (!hasGardenImages) return;

    try {
      const response = await fetch(`${pdp.dataset.productUrl || window.location.pathname}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const productJson = await response.json();
      const mediaById = new Map(
        (productJson.media || [])
          .filter((media) => media && media.media_type === 'image' && media.id && media.src)
          .map((media) => [String(media.id), media])
      );

      gallery.querySelectorAll('.gt-pdp__gallery-frame, .gt-pdp__gallery-img').forEach((frame, index) => {
        const media = mediaById.get(frame.dataset.mediaId);
        if (!media) return;

        let image = frame.querySelector('img');
        if (!image) {
          image = document.createElement('img');
          frame.appendChild(image);
        }

        const imageSrc = `${media.src}${media.src.includes('?') ? '&' : '?'}width=1400`;
        image.classList.add('gt-pdp__image');
        image.removeAttribute('srcset');
        image.src = imageSrc;
        image.alt = media.alt || productJson.title || '';
        image.loading = index <= 1 ? 'eager' : 'lazy';
        image.decoding = index <= 1 ? 'auto' : 'async';
        image.sizes = '(min-width: 1200px) 48vw, (min-width: 901px) 50vw, 100vw';
        image.width = media.width || 1400;
        image.height = media.height || image.width;
      });
    } catch (error) {
      console.warn('[Prowlet] Failed to restore product gallery media', error);
    }
  };

  restoreNonGardenPdpGallery();
  document.addEventListener('DOMContentLoaded', restoreNonGardenPdpGallery, { once: true });
  window.addEventListener('load', restoreNonGardenPdpGallery, { once: true });
  window.addEventListener('pageshow', restoreNonGardenPdpGallery);
  [700, 1800, 3200].forEach((delay) => window.setTimeout(restoreNonGardenPdpGallery, delay));
})();

(() => {
  const styleId = 'prowlet-mobile-final-layout-20260604';

  const css = `
    @media screen and (max-width: 749px) {
      html body #MainContent [id$="__featured_collection"] .collection {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }

      html body #MainContent [id$="__featured_collection"] .collection__title {
        padding-left: 16px !important;
        padding-right: 16px !important;
      }

      html body #MainContent [id$="__featured_collection"] .slider-component-full-width,
      html body #MainContent [id$="__featured_collection"] slider-component {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }

      html body #MainContent [id$="__featured_collection"] .slider {
        column-gap: 10px !important;
        margin: 0 !important;
        padding: 0 0 8px 16px !important;
        scroll-padding-left: 16px !important;
      }

      html body #MainContent [id$="__featured_collection"] .slider--tablet.grid--peek .grid__item,
      html body #MainContent [id$="__featured_collection"] .grid__item {
        flex: 0 0 calc(50vw - 22px) !important;
        max-width: calc(50vw - 22px) !important;
        width: calc(50vw - 22px) !important;
      }

      html body #MainContent [id$="__featured_collection"] .card__inner,
      html body #MainContent [id$="__featured_collection"] .card__media,
      html body #MainContent [id$="__featured_collection"] .card__media .media {
        aspect-ratio: 1 / 1 !important;
      }

      html body #MainContent [id$="__featured_collection"] .quick-add__submit {
        -webkit-appearance: none !important;
        align-items: center !important;
        appearance: none !important;
        box-sizing: border-box !important;
        display: flex !important;
        height: 40px !important;
        justify-content: center !important;
        line-height: 1 !important;
        max-height: 40px !important;
        min-height: 40px !important;
        padding: 0 0.7rem !important;
      }

      html body #MainContent [id$="__multicolumn_VQV8R3"] .multicolumn .page-width {
        max-width: none !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
      }

      html body #MainContent [id$="__multicolumn_VQV8R3"] .title-wrapper-with-link {
        padding-left: 16px !important;
        padding-right: 16px !important;
      }

      html body #MainContent [id$="__multicolumn_VQV8R3"] .slider-mobile-gutter {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }

      html body #MainContent [id$="__multicolumn_VQV8R3"] .multicolumn-list {
        column-gap: 10px !important;
        display: flex !important;
        flex-wrap: nowrap !important;
        margin: 0 !important;
        overflow-x: auto !important;
        padding: 0 0 12px 16px !important;
        scroll-padding-left: 16px !important;
        scroll-snap-type: x mandatory !important;
        scrollbar-width: none !important;
        -webkit-overflow-scrolling: touch !important;
      }

      html body #MainContent [id$="__multicolumn_VQV8R3"] .multicolumn-list::-webkit-scrollbar {
        display: none !important;
      }

      html body #MainContent [id$="__multicolumn_VQV8R3"] .multicolumn-list__item {
        flex: 0 0 calc(100vw - 42px) !important;
        max-width: calc(100vw - 42px) !important;
        scroll-snap-align: start !important;
        width: calc(100vw - 42px) !important;
      }

      html body #MainContent [id$="__multicolumn_VQV8R3"] .multicolumn-card,
      html body #MainContent [id$="__multicolumn_VQV8R3"] .multicolumn-card__image-wrapper,
      html body #MainContent [id$="__multicolumn_VQV8R3"] .media {
        margin-left: 0 !important;
        margin-right: 0 !important;
        width: 100% !important;
      }

      html body #MainContent [id$="__multicolumn_VQV8R3"] .media {
        aspect-ratio: 1 / 1 !important;
      }

      html body #MainContent [id$="__multicolumn_VQV8R3"] .media img {
        height: 100% !important;
        object-fit: cover !important;
        width: 100% !important;
      }
    }

    @media screen and (max-width: 900px) {
      html body .shopify-section .gt-pdp .gt-pdp__hero {
        padding: 0 0 28px !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__hero-grid {
        display: flex !important;
        flex-direction: column !important;
        gap: 0 !important;
        grid-template-columns: none !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__product-info-wrap {
        order: 1 !important;
        padding: 8px 24px 8px !important;
        width: 100% !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__product-info,
      html body .shopify-section .gt-pdp .gt-pdp__product-info.is-fixed,
      html body .shopify-section .gt-pdp .gt-pdp__product-info.is-bottom {
        bottom: auto !important;
        left: auto !important;
        max-height: none !important;
        overflow: visible !important;
        padding: 0 !important;
        position: relative !important;
        text-align: left !important;
        top: auto !important;
        width: auto !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__breadcrumb {
        margin: 0 0 6px !important;
        padding: 10px 12px 5px !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__product-info h1 {
        font-size: 34px !important;
        line-height: 1.08 !important;
        margin: 0 0 12px !important;
        text-align: left !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__title-meta-row {
        align-items: center !important;
        display: flex !important;
        flex-direction: row !important;
        flex-wrap: nowrap !important;
        gap: 6px !important;
        justify-content: flex-start !important;
        margin: 0 0 8px !important;
        overflow: visible !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__lead-inline {
        flex: 1 1 auto !important;
        font-size: 14px !important;
        line-height: 1.25 !important;
        margin: 0 !important;
        min-width: 0 !important;
        text-align: left !important;
        white-space: nowrap !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__reviews-inline {
        gap: 6px !important;
        justify-content: flex-start !important;
        line-height: 1.2 !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__price {
        font-size: 24px !important;
        line-height: 1 !important;
        margin: 0 0 24px !important;
        text-align: left !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__gallery {
        display: flex !important;
        gap: 0 !important;
        grid-template-columns: none !important;
        margin: 0 !important;
        order: 2 !important;
        overflow-x: auto !important;
        overflow-y: hidden !important;
        padding: 0 !important;
        scroll-snap-type: x mandatory !important;
        scrollbar-width: none !important;
        width: 100% !important;
        -webkit-overflow-scrolling: touch !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__gallery::-webkit-scrollbar {
        display: none !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__gallery-frame,
      html body .shopify-section .gt-pdp .gt-pdp__gallery-img {
        aspect-ratio: 1 / 1 !important;
        flex: 0 0 100% !important;
        min-height: 0 !important;
        scroll-snap-align: start !important;
        width: 100% !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__variant-options {
        display: grid !important;
        gap: 12px !important;
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        margin: 0 !important;
        width: 100% !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__variant-btn {
        align-items: center !important;
        display: flex !important;
        flex-direction: row !important;
        gap: 8px !important;
        justify-content: space-between !important;
        height: 52px !important;
        min-height: 52px !important;
        padding: 0 12px !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__variant-btn span {
        font-size: 14px !important;
        line-height: 1.2 !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__variant-btn small {
        font-size: 11px !important;
        line-height: 1.2 !important;
        text-align: right !important;
        white-space: nowrap !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__trust-mini {
        align-items: start !important;
        column-gap: 0 !important;
        display: grid !important;
        gap: 0 !important;
        grid-template-columns: repeat(3, max-content) !important;
        justify-content: space-between !important;
        margin: 0 !important;
        padding: 18px 0 20px !important;
        width: 100% !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item,
      html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item:nth-child(n + 2) {
        align-items: center !important;
        display: flex !important;
        flex-direction: column !important;
        gap: 8px !important;
        grid-template-columns: none !important;
        justify-content: flex-start !important;
        min-width: 0 !important;
        padding: 0 !important;
        text-align: center !important;
        width: auto !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item > img,
      html body .shopify-section .gt-pdp .gt-pdp__trust-icon {
        flex: 0 0 35px !important;
        height: 35px !important;
        margin: 0 auto !important;
        max-height: 35px !important;
        max-width: 35px !important;
        object-fit: contain !important;
        width: 35px !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item:first-child > img,
      html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item:first-child .gt-pdp__trust-icon {
        flex: 0 0 35px !important;
        height: 35px !important;
        margin: 0 auto !important;
        max-height: 35px !important;
        max-width: 35px !important;
        object-fit: contain !important;
        transform: none !important;
        width: 35px !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__trust-copy {
        display: block !important;
        min-width: 0 !important;
        text-align: center !important;
        width: 100% !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__trust-title {
        display: block !important;
        font-size: 9.5px !important;
        font-weight: 650 !important;
        letter-spacing: 0.08em !important;
        line-height: 1.2 !important;
        text-align: center !important;
        white-space: normal !important;
      }

      html body .shopify-section .gt-pdp .gt-pdp__trust-sub {
        display: block !important;
        font-size: 10px !important;
        letter-spacing: 0.02em !important;
        line-height: 1.25 !important;
        margin-top: 4px !important;
        text-align: center !important;
        white-space: normal !important;
      }
    }
  `;

  const ensureStyle = () => {
    let style = document.getElementById(styleId);
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }
    if (style.textContent !== css) style.textContent = css;
  };

  const prepareGardenTunnelVideo = (video) => {
    video.autoplay = false;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.removeAttribute('autoplay');
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('preload', 'none');
  };

  const playGardenTunnelVideo = (video) => {
    prepareGardenTunnelVideo(video);
    if (video.readyState === 0) video.load();
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  };

  const observeGardenTunnelVideos = () => {
    const videos = Array.from(document.querySelectorAll('.gt-pdp__ugc-video, .bm-ugc__video'))
      .filter((video) => !video.closest('[data-gt-ugc-carousel]'));
    if (!videos.length) return;

    videos.forEach(prepareGardenTunnelVideo);
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          playGardenTunnelVideo(video);
        } else {
          video.pause();
        }
      });
    }, { rootMargin: '120px 0px', threshold: 0.2 });

    videos.forEach((video) => {
      if (video.dataset.prowletUgcLazyReady === 'true') return;
      video.dataset.prowletUgcLazyReady = 'true';
      video.addEventListener('click', () => playGardenTunnelVideo(video));
      observer.observe(video);
    });
  };

  const apply = () => {
    ensureStyle();
    observeGardenTunnelVideos();
  };

  apply();
  document.addEventListener('DOMContentLoaded', apply, { once: true });
  window.addEventListener('load', apply, { once: true });
  window.addEventListener('pageshow', apply);
})();

(() => {
  const styleId = 'prowlet-header-bestseller-align-20260526-js';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @media screen and (min-width: 990px) {
      .section-header .header__icons {
        gap: 12px !important;
        margin-right: -14px !important;
        transform: none !important;
      }

      .section-header .header__icons .header__search {
        display: flex !important;
      }

      .section-header .header__icons .header__search,
      .section-header .header__icons .header__search details,
      .section-header .header__icons .header__search summary,
      .section-header .header__icons .header__icon--search,
	      .section-header .header__icons .header__icon--account {
	        width: 20px !important;
	        min-width: 20px !important;
	      }

		      .section-header .header__icons .header__icon--search {
		        transform: translateX(15px) !important;
		      }

		      .section-header .header__icons .header__icon--account {
		        transform: translateX(14px) !important;
		      }

	      .section-header .header > .header__search {
        display: none !important;
        margin-right: 0 !important;
        transform: none !important;
      }

      .section-header #cart-icon-bubble {
        transform: none !important;
      }
    }

	    @media screen and (min-width: 750px) {
	      html body #shopify-section-template--24869874073896__image_banner_J9gppc .banner__text,
	      html body #shopify-section-template--24869874073896__image_banner_J9gppc .banner__text p,
	      html body #shopify-section-template--24869874073896__image_banner_J9gppc .banner__buttons.banner__buttons .button.button {
	        font-size: 18px !important;
	      }

	      [id$="__featured_collection"] .title,
	      [id$="__multicolumn_VQV8R3"] .title {
	        font-size: 33px !important;
	        line-height: 42.9px !important;
	        letter-spacing: 0.495px !important;
	      }

	      [id$="__featured_collection"] .product-card-wrapper .card-information > .price,
	      [id$="__featured_collection"] .product-card-wrapper .card-information > .price *,
	      [id$="__featured_collection"] .product-card-wrapper .card-information > .price .price-item {
	        font-size: 14px !important;
	        line-height: 19.6px !important;
	      }

	      [id$="__featured_collection"] .product-card-wrapper .card__heading,
	      [id$="__featured_collection"] .product-card-wrapper .card__heading a {
	        font-size: 15px !important;
	        line-height: 20.25px !important;
	      }

	      [id$="__featured_collection"] .product-card-wrapper .rating-star {
	        font-size: 14px !important;
	        line-height: 14px !important;
	      }

	      [id$="__featured_collection"] .product-card-wrapper .rating-count,
	      [id$="__featured_collection"] .product-card-wrapper .rating-count span[aria-hidden="true"] {
	        font-size: 14px !important;
	        line-height: 18px !important;
	      }

	      [id$="__featured_collection"] .quick-add__submit {
	        font-family: Outfit, sans-serif !important;
	        font-size: 14px !important;
	        font-weight: 400 !important;
	        line-height: 16.8px !important;
	        letter-spacing: 0.89375px !important;
	      }

	      [id$="__multicolumn_VQV8R3"] .multicolumn-card__info h3 {
	        font-size: 15px !important;
	        line-height: 22.5px !important;
	      }

	      [id$="__multicolumn_VQV8R3"] .multicolumn-card__info .link,
	      [id$="__multicolumn_VQV8R3"] .multicolumn-card__info a {
	        font-size: 15px !important;
	        line-height: 23px !important;
	      }
	    }

	    @media screen and (min-width: 990px) {
	      html body #shopify-section-template--24869874073896__image_banner_J9gppc .banner__buttons.banner__buttons .button.button {
	        min-height: 54px !important;
	        padding-left: 36px !important;
	        padding-right: 36px !important;
	      }
	    }

	    @media screen and (max-width: 749px) {
      [id$="__featured_collection"] .product-card-wrapper .card-information > .price,
      [id$="__featured_collection"] .product-card-wrapper .card-information > .price *,
      [id$="__featured_collection"] .product-card-wrapper .card-information > .price .price-item {
        font-size: 13px !important;
      }
    }
  `;
  document.head.appendChild(style);
})();

(() => {
  const styleId = 'prowlet-mobile-final-layout-20260604';

  const moveFinalMobileStyleToEnd = () => {
    const style = document.getElementById(styleId);
    if (style) document.head.appendChild(style);
  };

  moveFinalMobileStyleToEnd();
  requestAnimationFrame(moveFinalMobileStyleToEnd);
  document.addEventListener('DOMContentLoaded', moveFinalMobileStyleToEnd, { once: true });
  window.addEventListener('load', moveFinalMobileStyleToEnd, { once: true });
  window.addEventListener('pageshow', moveFinalMobileStyleToEnd);
  setTimeout(moveFinalMobileStyleToEnd, 300);
  setTimeout(moveFinalMobileStyleToEnd, 1200);
})();

(() => {
  const styleId = 'prowlet-sticky-selected-options-20260901';

  const ensureStyle = () => {
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .gt-pdp__sticky-cta[data-selected-options-ready='true'] {
        background: var(--warm-white) !important;
        border-radius: 0 !important;
        border-top: 1px solid var(--tan-line) !important;
        bottom: 0 !important;
        box-shadow: 0 -8px 28px rgba(44, 24, 16, .08) !important;
        color: var(--cocoa) !important;
        gap: 18px !important;
        left: 0 !important;
        min-height: 86px;
        padding: 12px max(24px, calc((100vw - 1440px) / 2)) !important;
        right: 0 !important;
        width: 100% !important;
        z-index: 10000 !important;
      }
      html body > cart-drawer.active ~ .gt-pdp__sticky-cta {
        display: none !important;
      }
      .gt-pdp__sticky-product {
        align-items: center;
        display: flex;
        flex: 1 1 auto;
        gap: 14px;
        min-width: 0;
      }
      .gt-pdp__sticky-thumb {
        background: var(--cream);
        border-radius: 10px;
        flex: 0 0 60px;
        height: 60px;
        object-fit: cover;
        width: 60px;
      }
      .gt-pdp__sticky-summary {
        display: block;
        min-width: 0;
      }
      .gt-pdp__sticky-title {
        color: var(--cocoa);
        font-family: var(--font-body);
        font-size: 16px;
        font-weight: 600;
        line-height: 1.25;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .gt-pdp__sticky-meta {
        align-items: baseline;
        display: flex;
        gap: 9px;
        margin-top: 4px;
        min-width: 0;
      }
      .gt-pdp__sticky-cta[data-selected-options-ready='true'] .gt-pdp__sticky-price {
        color: var(--cocoa) !important;
        font-family: var(--font-body) !important;
        font-size: 14px !important;
        font-weight: 600 !important;
      }
      .gt-pdp__sticky-cta[data-selected-options-ready='true'] .gt-pdp__sticky-compare-price {
        color: rgba(44, 24, 16, .52) !important;
      }
      .gt-pdp__sticky-options {
        font-family: var(--font-body);
        font-size: 12px;
        font-weight: 400;
        line-height: 1.3;
        max-width: min(460px, 40vw);
        opacity: .7;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .gt-pdp__sticky-quantity {
        align-items: center;
        border: 1px solid var(--tan-line);
        border-radius: 10px;
        display: grid;
        flex: 0 0 auto;
        grid-template-columns: 42px 42px 42px;
        height: 52px;
        overflow: hidden;
      }
      .gt-pdp__sticky-quantity button,
      .gt-pdp__sticky-quantity-value {
        align-items: center;
        background: transparent;
        border: 0;
        color: var(--cocoa);
        display: flex;
        font-family: var(--font-body);
        font-size: 16px;
        height: 100%;
        justify-content: center;
        padding: 0;
      }
      .gt-pdp__sticky-quantity-value {
        appearance: textfield;
        outline: 0;
        text-align: center;
        width: 42px;
      }
      .gt-pdp__sticky-quantity-value::-webkit-inner-spin-button,
      .gt-pdp__sticky-quantity-value::-webkit-outer-spin-button { appearance: none; margin: 0; }
      .gt-pdp__sticky-quantity button { cursor: pointer; }
      .gt-pdp__sticky-quantity button:hover { background: rgba(44, 24, 16, .05); }
      .gt-pdp__sticky-cta[data-selected-options-ready='true'] .gt-pdp__sticky-divider { display: none; }
      .gt-pdp__sticky-cta[data-selected-options-ready='true'] .gt-pdp__sticky-btn {
        align-items: center;
        background: var(--cocoa) !important;
        border-radius: 10px !important;
        color: var(--warm-white) !important;
        display: flex;
        flex: 0 0 auto !important;
        font-size: 14px !important;
        height: 52px;
        justify-content: center;
        letter-spacing: .03em !important;
        min-width: 190px;
        padding: 0 28px !important;
        text-transform: none !important;
      }
      @media screen and (max-width: 900px) {
        .gt-pdp__sticky-cta[data-selected-options-ready='true'] {
          opacity: 0 !important;
          pointer-events: none !important;
          visibility: hidden !important;
        }
        .gt-pdp__sticky-cta.is-visible[data-selected-options-ready='true'] {
          opacity: 1 !important;
          pointer-events: auto !important;
          visibility: visible !important;
        }
      }
      @media screen and (max-width: 749px) {
        .gt-pdp__sticky-cta[data-selected-options-ready='true'] {
          box-sizing: border-box !important;
          display: grid !important;
          gap: 6px !important;
          grid-template-columns: 100px minmax(0, 1fr);
          grid-template-rows: 34px 40px;
          height: 100px;
          min-height: 100px;
          padding: 7px 12px 9px !important;
        }
        .gt-pdp__sticky-product {
          grid-column: 1 / -1;
          gap: 8px;
        }
        .gt-pdp__sticky-thumb {
          border-radius: 7px;
          flex-basis: 34px;
          height: 34px;
          width: 34px;
        }
        .gt-pdp__sticky-title { font-size: 13px; }
        .gt-pdp__sticky-meta { gap: 6px; margin-top: 0; }
        .gt-pdp__sticky-cta[data-selected-options-ready='true'] .gt-pdp__sticky-price {
          font-size: 12px !important;
        }
        .gt-pdp__sticky-options {
          color: var(--cocoa);
          font-size: 10px;
          max-width: 68vw;
        }
        .gt-pdp__sticky-quantity {
          grid-template-columns: 32px 36px 32px;
          height: 40px;
        }
        .gt-pdp__sticky-quantity-value { width: 36px; }
        .gt-pdp__sticky-cta[data-selected-options-ready='true'] .gt-pdp__sticky-btn {
          height: 40px;
          min-width: 0;
          padding: 0 16px !important;
          width: 100%;
        }
      }
    `;
    document.head.appendChild(style);
  };

  const enhanceStickyOptions = (root = document) => {
    root.querySelectorAll('.gt-pdp').forEach((section) => {
      const sticky = section.querySelector('.gt-pdp__sticky-cta');
      const price = sticky?.querySelector('.gt-pdp__sticky-price');
      const heroCta = section.querySelector('.gt-pdp__cta-add');
      if (!sticky || !price || sticky.dataset.selectedOptionsReady === 'true') return;

      const product = document.createElement('span');
      const thumb = document.createElement('img');
      const summary = document.createElement('span');
      const title = document.createElement('span');
      const meta = document.createElement('span');
      const options = document.createElement('span');
      const quantity = document.createElement('span');
      const decrease = document.createElement('button');
      const quantityValue = document.createElement('input');
      const increase = document.createElement('button');
      const galleryImage = section.querySelector('.gt-pdp__gallery img');
      const currentFormId = sticky.querySelector('.gt-pdp__sticky-btn')?.getAttribute('form');
      const productForm = currentFormId ? document.getElementById(currentFormId) : null;

      product.className = 'gt-pdp__sticky-product';
      thumb.className = 'gt-pdp__sticky-thumb';
      thumb.src = galleryImage?.currentSrc || galleryImage?.src || '';
      thumb.alt = galleryImage?.alt || '';
      summary.className = 'gt-pdp__sticky-summary';
      title.className = 'gt-pdp__sticky-title';
      title.textContent = section.querySelector('h1')?.textContent?.trim() || '';
      meta.className = 'gt-pdp__sticky-meta';
      options.className = 'gt-pdp__sticky-options';
      options.dataset.gtStickyOptions = '';
      options.setAttribute('aria-live', 'polite');
      quantity.className = 'gt-pdp__sticky-quantity';
      quantity.setAttribute('aria-label', 'Quantity');
      decrease.type = 'button';
      decrease.textContent = '−';
      decrease.setAttribute('aria-label', 'Decrease quantity');
      quantityValue.className = 'gt-pdp__sticky-quantity-value';
      quantityValue.type = 'number';
      quantityValue.name = 'quantity';
      quantityValue.min = '1';
      quantityValue.value = '1';
      quantityValue.setAttribute('value', '1');
      quantityValue.setAttribute('aria-label', 'Quantity');
      if (currentFormId) quantityValue.setAttribute('form', currentFormId);
      productForm?.querySelectorAll('input[name="quantity"]').forEach((input) => {
        input.disabled = true;
      });
      increase.type = 'button';
      increase.textContent = '+';
      increase.setAttribute('aria-label', 'Increase quantity');

      sticky.prepend(product);
      product.append(thumb, summary);
      summary.append(title, meta);
      meta.append(price, options);
      quantity.append(decrease, quantityValue, increase);
      sticky.querySelector('.gt-pdp__sticky-btn')?.before(quantity);

      const syncThumbnail = () => {
        let variants = [];
        try {
          variants = JSON.parse(section.querySelector('[data-gt-variants]')?.textContent || '[]');
        } catch (error) {
          variants = [];
        }
        const selectedOptions = Array.from(section.querySelectorAll('[data-gt-option-group]')).map((group) => {
          const checked = group.querySelector('[data-gt-option-input]:checked');
          return checked?.dataset.optionValue || checked?.value || '';
        });
        const selectedVariant = variants.find((variant) =>
          variant.options?.every((value, index) => value === selectedOptions[index])
        );
        const mediaId = String(selectedVariant?.featuredMediaId || '');
        const mediaFrame = mediaId
          ? Array.from(section.querySelectorAll('.gt-pdp__gallery [data-media-id]')).find((frame) =>
              frame.dataset.mediaId === mediaId && !frame.classList.contains('gt-pdp__gallery-clone')
            )
          : null;
        const mediaImage = mediaFrame?.matches('img') ? mediaFrame : mediaFrame?.querySelector('img');
        if (mediaImage) {
          thumb.src = mediaImage.currentSrc || mediaImage.src || thumb.src;
          thumb.alt = mediaImage.alt || thumb.alt;
          return;
        }
        if (selectedVariant?.featuredMediaSrc) {
          thumb.src = selectedVariant.featuredMediaSrc;
          return;
        }

        const selectedColor = section.querySelector(
          '.gt-pdp__variant-group--photo-color [data-gt-option-input]:checked'
        );
        const swatchDot = selectedColor?.closest('.gt-pdp__variant-swatch')?.querySelector(
          '.gt-pdp__variant-swatch-dot--image'
        );
        const swatchImage = swatchDot?.style.getPropertyValue('--gt-swatch-image').trim() || '';
        const imageMatch = swatchImage.match(/url\((['"]?)(.*?)\1\)/);
        if (imageMatch?.[2]) thumb.src = imageMatch[2];
      };

      const sync = () => {
        options.textContent = Array.from(section.querySelectorAll('[data-gt-option-input]:checked'))
          .map((input) => input.dataset.optionValue || input.value)
          .filter(Boolean)
          .join(' · ');
        syncThumbnail();
      };

      const setQuantity = (nextQuantity) => {
        const value = Math.max(1, nextQuantity);
        quantityValue.value = String(value);
        quantityValue.setAttribute('value', String(value));
      };

      section.addEventListener('change', (event) => {
        if (event.target.matches('[data-gt-option-input]')) requestAnimationFrame(sync);
      });
      decrease.addEventListener('click', () => setQuantity(Number(quantityValue.value) - 1));
      increase.addEventListener('click', () => setQuantity(Number(quantityValue.value) + 1));
      quantityValue.addEventListener('change', () => setQuantity(Number(quantityValue.value) || 1));
      setQuantity(1);
      sticky.dataset.selectedOptionsReady = 'true';
      sticky.dataset.productHandle = section.dataset.productHandle || '';
      sticky.style.setProperty('z-index', '10000', 'important');
      const sectionStyle = getComputedStyle(section);
      ['--warm-white', '--cocoa', '--tan-line', '--cream', '--font-body', '--font-heading'].forEach((property) => {
        sticky.style.setProperty(property, sectionStyle.getPropertyValue(property));
      });
      document.body.appendChild(sticky);
      sync();
    });
  };

  const apply = (root) => {
    ensureStyle();
    enhanceStickyOptions(root);
  };

  ensureStyle();
  if (document.readyState === 'complete') {
    apply(document);
  } else {
    document.addEventListener('DOMContentLoaded', () => apply(document), { once: true });
  }
  document.addEventListener('shopify:section:load', (event) => apply(event.target));
})();

(() => {
  if (window.location.pathname.replace(/\/$/, '') !== '/pages/reviews') return;

  const removeLegacyReviewSchema = () => {
    document.querySelectorAll('script[type="application/ld+json"]').forEach((script) => {
      const text = script.textContent || '';
      const isLegacyReviewsSchema =
        text.includes('Prowlet customer reviews') ||
        (text.includes('"itemReviewed"') && text.includes('"@type": "Review"'));

      if (isLegacyReviewsSchema) {
        script.remove();
      }
    });
  };

  removeLegacyReviewSchema();
  document.addEventListener('DOMContentLoaded', removeLegacyReviewSchema, { once: true });
  window.addEventListener('load', removeLegacyReviewSchema, { once: true });
  setTimeout(removeLegacyReviewSchema, 500);
  setTimeout(removeLegacyReviewSchema, 2000);

  new MutationObserver(removeLegacyReviewSchema).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();

(() => {
  const patchWelcomePopup = () => {
    const Popup = customElements.get('welcome-popup');
    if (!Popup || Popup.prototype.__prowletWelcomeFormFix20260609) return;

    Popup.prototype.handleSubmit = async function (event) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

      const submitBtn = this.form && this.form.querySelector('[type="submit"]');
      const btnLabel = this.form && this.form.querySelector('[data-welcome-button-label]');
      const originalLabel = btnLabel ? btnLabel.textContent : 'Send me the code';

      if (submitBtn) submitBtn.disabled = true;
      if (btnLabel) btnLabel.textContent = '...';
      if (this.error) this.error.hidden = true;
      if (this.form) this.form.setAttribute('data-nocaptcha', 'true');

      const data = new FormData(this.form);
      if (!data.has('contact[accepts_marketing]')) data.append('contact[accepts_marketing]', 'true');
      const params = new URLSearchParams();
      data.forEach((value, key) => params.append(key, value));

      const endpoint = new URL((this.form && this.form.getAttribute('action')) || '/contact', window.location.origin);
      endpoint.hash = '';

      try {
        await fetch(endpoint.toString(), {
          method: 'POST',
          body: params.toString(),
          headers: {
            Accept: 'text/html,application/xhtml+xml',
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          },
          credentials: 'same-origin',
          redirect: 'follow',
        });
      } catch (error) {
        console.warn('[welcome-popup] subscribe fetch failed', error);
        if (this.error) {
          this.error.textContent = 'Something went wrong. Please try again.';
          this.error.hidden = false;
        }
        if (submitBtn) submitBtn.disabled = false;
        if (btnLabel) btnLabel.textContent = originalLabel;
        return;
      }

      if (typeof this.setState === 'function') this.setState('subscribed');
      if (typeof this.showSuccessState === 'function') {
        this.showSuccessState();
      } else {
        if (this.form) this.form.hidden = true;
        if (this.success) this.success.hidden = false;
      }
    };

    Popup.prototype.__prowletWelcomeFormFix20260609 = true;
  };

  if (window.customElements) {
    customElements.whenDefined('welcome-popup').then(patchWelcomePopup).catch(() => {});
    patchWelcomePopup();
  }
})();

(() => {
  const styleId = 'gt-pdp-mobile-tuning-20260605';

  const ensurePdpMobileTuning = () => {
    let style = document.getElementById(styleId);
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @media screen and (max-width: 900px) {
          html body .shopify-section .gt-pdp .gt-pdp__breadcrumb,
          html body .shopify-section .gt-pdp .gt-pdp__eyebrow {
            padding: 5px 12px !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__lead-inline {
            font-size: 14.5px !important;
            line-height: 1.25 !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__hero {
            padding-bottom: 0 !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__mobile-purchase-wrap {
            padding: 18px 24px 0 !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__variant-btn,
          html body .shopify-section .gt-pdp .gt-pdp__cta-add,
          html body .shopify-section .gt-pdp .product-form__submit,
          html body .shopify-section .gt-pdp button[name='add'],
          html body .shopify-section .gt-pdp .shopify-payment-button__button,
          html body .shopify-section .gt-pdp .shopify-payment-button__button--unbranded {
            height: 50px !important;
            min-height: 50px !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__variant-btn:not(.is-selected):not(.is-disabled) {
            border: 1px solid rgba(89, 53, 24, 0.28) !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__sticky-btn {
            min-height: 50px !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__shop-pay-wrap,
          html body .shopify-section .gt-pdp .gt-pdp__shop-pay-wrap .shopify-payment-button,
          html body .shopify-section .gt-pdp .shopify-payment-button {
            height: auto !important;
            min-height: 72px !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__trust-mini {
            align-items: start !important;
            grid-template-columns: repeat(3, max-content) !important;
            justify-content: space-between !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item,
          html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item:nth-child(n + 2) {
            align-items: center !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 8px !important;
            justify-content: flex-start !important;
            padding: 0 !important;
            text-align: center !important;
            width: auto !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item > img,
          html body .shopify-section .gt-pdp .gt-pdp__trust-icon,
          html body #MainContent .shopify-section .gt-pdp.gt-pdp .gt-pdp__trust-mini .gt-pdp__trust-icon,
          html body #MainContent .shopify-section .gt-pdp.gt-pdp .gt-pdp__trust-mini .gt-pdp__trust-mini-item > img,
          html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item:nth-child(n + 2) > img {
            flex: 0 0 35px !important;
            height: 35px !important;
            max-height: 35px !important;
            max-width: 35px !important;
            margin: 0 auto !important;
            object-fit: contain !important;
            width: 35px !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item:first-child > img,
          html body .shopify-section .gt-pdp .gt-pdp__trust-mini-item:first-child .gt-pdp__trust-icon,
          html body #MainContent .shopify-section .gt-pdp.gt-pdp .gt-pdp__trust-mini .gt-pdp__trust-mini-item:first-child > img,
          html body #MainContent .shopify-section .gt-pdp.gt-pdp .gt-pdp__trust-mini .gt-pdp__trust-mini-item:first-child .gt-pdp__trust-icon {
            flex: 0 0 35px !important;
            height: 35px !important;
            margin: 0 auto !important;
            max-height: 35px !important;
            max-width: 35px !important;
            transform: none !important;
            width: 35px !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__trust-title {
            font-size: 9.5px !important;
            font-weight: 650 !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__trust-sub {
            font-size: 10px !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__ugc {
            margin: 20px 0 0 !important;
            padding-top: 0 !important;
            padding-bottom: 28px !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__mobile-purchase-wrap {
            padding-bottom: 0 !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__accordion-block {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__accordion-item:last-child {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }

          html body .shopify-section .gt-pdp .gt-pdp__ugc .gt-pdp__section-title {
            margin-top: 0 !important;
          }
        }
      `;
    }

    document.head.appendChild(style);
  };

  ensurePdpMobileTuning();
  requestAnimationFrame(ensurePdpMobileTuning);
  document.addEventListener('DOMContentLoaded', ensurePdpMobileTuning, { once: true });
  window.addEventListener('load', ensurePdpMobileTuning, { once: true });
  window.addEventListener('pageshow', ensurePdpMobileTuning);
  setTimeout(ensurePdpMobileTuning, 300);
  setTimeout(ensurePdpMobileTuning, 1200);
  setTimeout(ensurePdpMobileTuning, 2500);
})();

(() => {
  const styleId = 'prowlet-announcement-arrow-align-20260524-js';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @media screen and (min-width: 750px) {
      .announcement-bar-section .utility-bar .page-width {
        max-width: 100% !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
      }

      .announcement-bar-section .announcement-bar .slider-button {
        width: 44px !important;
        min-width: 44px !important;
        height: 33px !important;
      }

      .announcement-bar-section .announcement-bar .slider-button--prev {
        left: 5px !important;
      }

      .announcement-bar-section .announcement-bar .slider-button--next {
        right: 5px !important;
      }

      .announcement-bar-section .announcement-bar .slider-button .svg-wrapper,
      .announcement-bar-section .announcement-bar .slider-button .icon {
        width: 12px !important;
        height: 12px !important;
      }
    }

    @media screen and (max-width: 749px) {
      .announcement-bar-section .announcement-bar .slider-button {
        align-items: center !important;
        display: flex !important;
        width: 32px !important;
        min-width: 32px !important;
        height: 28px !important;
        justify-content: center !important;
        margin: 0 !important;
        padding: 0 !important;
        top: 50% !important;
        transform: translateY(-50%) !important;
      }

      .announcement-bar-section .announcement-bar .slider-button--prev {
        left: 11px !important;
        right: auto !important;
      }

      .announcement-bar-section .announcement-bar .slider-button--next {
        left: auto !important;
        right: 11px !important;
      }

      .announcement-bar-section .announcement-bar .slider-button .svg-wrapper,
      .announcement-bar-section .announcement-bar .slider-button .icon {
        width: 10px !important;
        height: 10px !important;
      }

      .section-header #cart-icon-bubble .cart-count-bubble {
        align-items: center !important;
        background: transparent !important;
        border: 0 !important;
        border-radius: 0 !important;
        bottom: auto !important;
        color: #2C1810 !important;
        display: flex !important;
        font-size: 10px !important;
        font-weight: 500 !important;
        height: 12px !important;
        justify-content: center !important;
        left: 50% !important;
        letter-spacing: 0 !important;
        line-height: 10px !important;
        margin: 0 !important;
        min-width: 12px !important;
        padding: 0 !important;
        position: absolute !important;
        right: auto !important;
        text-align: center !important;
        top: calc(50% + 2.5px) !important;
        transform: translate(-50%, -50%) !important;
        width: auto !important;
      }

      .section-header #cart-icon-bubble .cart-count-bubble span[aria-hidden='true'] {
        display: block !important;
        font: inherit !important;
        height: 10px !important;
        line-height: 10px !important;
        text-align: center !important;
      }
    }
  `;
  document.head.appendChild(style);
})();

(() => {
  const styleId = 'prowlet-mobile-final-layout-20260604';

  const moveFinalMobileStyleToEnd = () => {
    const style = document.getElementById(styleId);
    if (style) document.head.appendChild(style);
  };

  moveFinalMobileStyleToEnd();
  requestAnimationFrame(moveFinalMobileStyleToEnd);
  document.addEventListener('DOMContentLoaded', moveFinalMobileStyleToEnd, { once: true });
  window.addEventListener('load', moveFinalMobileStyleToEnd, { once: true });
  window.addEventListener('pageshow', moveFinalMobileStyleToEnd);
  setTimeout(moveFinalMobileStyleToEnd, 300);
  setTimeout(moveFinalMobileStyleToEnd, 1200);
})();
